import { Card, Direction, Suit, Rank } from '../../state/ArenaTypes';
import { GameTableConfig } from '../../engine/tableConfig/types';
import { areSuitsEqual, normalizeSuit, isRedSuit } from '../../engine/CardSuits';
import { BaseHandSortingStrategy } from '../common/BaseHandSortingStrategy';
import { UPTOWN_POWER, DOWNTOWN_POWER } from './BidWhistConstants';

/**
 * Normalizes input direction to 'UP' or 'DOWN'.
 * Supports variations: 'UP', 'DOWN', 'Uptown', 'Downtown', 'up', 'down', 'U', 'D'.
 */
export function normalizeDirection(direction?: Direction | string | null): 'UP' | 'DOWN' {
  if (!direction) return 'UP';
  const clean = direction.trim().toUpperCase();
  if (clean === 'DOWN' || clean === 'DOWNTOWN' || clean === 'D') {
    return 'DOWN';
  }
  return 'UP';
}

/**
 * Checks whether a card is a Joker (Big Joker or Little Joker).
 */
export function isJokerCard(card: Card): boolean {
  if (!card) return false;
  if (card.isBigJoker || card.isLittleJoker) return true;
  if (card.rank === 'BJ' || card.rank === 'LJ') return true;
  if (card.id === 'BJ' || card.id === 'LJ') return true;
  const suit = (card.suit || '').toString().toUpperCase();
  if (suit === 'JOKER' || suit === 'J') return true;
  return false;
}

/**
 * Returns true if the Joker card is the Big Joker.
 */
export function isBigJoker(card: Card): boolean {
  if (!card) return false;
  if (card.isBigJoker) return true;
  if (card.rank === 'BJ' || card.id === 'BJ') return true;
  const val = (card.displayValue || '').toLowerCase();
  return val.includes('big');
}

/**
 * Comprehensive Bid Whist Hand Sorting Strategy.
 *
 * Implements authoritative Bid Whist tournament hand sorting conventions:
 * 1. Alternating Red and Black suits across the fan to maximize contrast and eliminate visual misplays.
 * 2. Direction-aware rank power:
 *    - Uptown (High): BJ (16) > LJ (15) > A (14) > K (13) > Q (12) > J (11) > 10..2
 *    - Downtown (Low): BJ (16) > LJ (15) > A (14) > 2 (13) > 3 (12) > 4 (11) > 5..10 > J > Q > K (2)
 * 3. Trump Suite Hierarchy:
 *    - In Suited Trump contracts: Trump suit cards + Jokers are ALWAYS grouped together on the far right.
 *    - The transition from non-trump suits into Trump strictly maintains Red/Black alternation.
 *    - Within the Trump group, cards are ordered ascending so the highest boss card (Big Joker)
 *      sits on the top/rightmost spot of the fan.
 * 4. No-Trump Contracts:
 *    - Pure 4-suit or 3-suit Red/Black alternation.
 *    - Jokers placed on the far right as unsuited / kitty discard cards.
 */
export class BidWhistHandSortingStrategy extends BaseHandSortingStrategy {
  /**
   * Evaluates the relative power of a card rank based on direction.
   */
  public getRankPower(rank: string, direction: Direction = 'UP'): number {
    const isDown = normalizeDirection(direction) === 'DOWN';
    const powerMap = isDown ? DOWNTOWN_POWER : UPTOWN_POWER;
    return powerMap[rank] ?? 0;
  }

  /**
   * Determines if a card is Trump under active Bid Whist contract rules.
   */
  public isTrump(card: Card, activeTrump?: string | null): boolean {
    if (!card) return false;
    if (isJokerCard(card)) {
      // In Bid Whist, Jokers are only Trump if a suit is declared.
      // In No-Trump, Jokers are unsuited (must be discarded or played specially).
      if (!activeTrump || activeTrump === 'NT' || activeTrump === 'NoTrump' || activeTrump === 'NO_TRUMP') {
        return false;
      }
      return true;
    }

    if (!activeTrump || activeTrump === 'NT' || activeTrump === 'NoTrump' || activeTrump === 'NO_TRUMP') {
      return false;
    }

    return areSuitsEqual(card.suit, activeTrump);
  }

  /**
   * Sorts player hand according to Bid Whist tournament rules.
   */
  public sortHand(
    hand: Card[],
    direction: Direction | string = 'UP',
    trumpSuit?: string | null,
    _config?: GameTableConfig
  ): Card[] {
    if (!hand || hand.length === 0) return [];

    // Filter valid cards defensively
    const validCards = hand.filter((c): c is Card => Boolean(c && (c.id || c.rank)));
    if (validCards.length === 0) return [];

    const normDir = normalizeDirection(direction);
    const isDowntown = normDir === 'DOWN';
    const rankPower = isDowntown ? DOWNTOWN_POWER : UPTOWN_POWER;

    // Bucket cards by suit
    const spades: Card[] = [];
    const hearts: Card[] = [];
    const clubs: Card[] = [];
    const diamonds: Card[] = [];
    const jokers: Card[] = [];

    validCards.forEach(c => {
      if (isJokerCard(c)) {
        jokers.push(c);
      } else {
        const norm = normalizeSuit(c.suit);
        if (norm === 'Spades') spades.push(c);
        else if (norm === 'Hearts') hearts.push(c);
        else if (norm === 'Clubs') clubs.push(c);
        else if (norm === 'Diamonds') diamonds.push(c);
        else {
          // Fallback to clubs for unexpected suit labels
          clubs.push(c);
        }
      }
    });

    // Sort cards within suit ascending by rank power so boss cards appear on top / far right
    const sortCards = (cards: Card[]) =>
      cards.sort((a, b) => (rankPower[a.rank] ?? 0) - (rankPower[b.rank] ?? 0));

    sortCards(spades);
    sortCards(hearts);
    sortCards(clubs);
    sortCards(diamonds);

    // Jokers: Little Joker first, then Big Joker
    jokers.sort((a, b) => {
      const aPower = isBigJoker(a) ? 16 : 15;
      const bPower = isBigJoker(b) ? 16 : 15;
      return aPower - bPower;
    });

    const rawTrump = trumpSuit ? trumpSuit.trim() : null;
    const isNoTrumpContract = Boolean(!rawTrump || rawTrump === 'NT' || rawTrump === 'NoTrump' || rawTrump === 'NO_TRUMP');
    const normTrump = isNoTrumpContract ? null : normalizeSuit(rawTrump!);
    const hasTrumpSuit = Boolean(normTrump && normTrump !== 'NoTrump');

    const suitCardMap: Record<string, Card[]> = {
      Spades: spades,
      Hearts: hearts,
      Clubs: clubs,
      Diamonds: diamonds,
      Joker: jokers
    };

    const trumpCards = hasTrumpSuit ? (suitCardMap[normTrump!] || []) : [];
    const hasTrumpGroup = hasTrumpSuit && (trumpCards.length > 0 || jokers.length > 0);

    // Determine present non-trump suits with >= 1 card
    const canonicalSuits = ['Spades', 'Hearts', 'Clubs', 'Diamonds'];
    const presentNonTrumpSuits = canonicalSuits.filter(
      s => (!hasTrumpSuit || s !== normTrump) && (suitCardMap[s]?.length || 0) > 0
    );

    const orderedSuits = this.getAlternatingSuitOrder(presentNonTrumpSuits, normTrump);

    const result: Card[] = [];

    // Append non-trump suits in alternating order
    orderedSuits.forEach(suit => {
      const cards = suitCardMap[suit];
      if (cards && cards.length > 0) {
        result.push(...cards);
      }
    });

    // Append Trump group on the far right:
    // Low trumps -> High trumps -> Little Joker -> Big Joker (sitting proudly on the far right)
    if (hasTrumpGroup) {
      if (trumpCards.length > 0) {
        result.push(...trumpCards);
      }
      if (jokers.length > 0) {
        result.push(...jokers);
      }
    } else if (jokers.length > 0) {
      // In No-Trump or unsuited hands, Jokers are placed on the far right
      result.push(...jokers);
    }

    return result;
  }
}

export const bidWhistHandSortingStrategy = new BidWhistHandSortingStrategy();

/**
 * Standalone convenience sorting function matching BidWhistHandSortingStrategy.
 */
export function sortBidWhistHand(
  hand: Card[],
  direction: Direction | string = 'UP',
  trumpSuit?: string | null,
  config?: GameTableConfig
): Card[] {
  return bidWhistHandSortingStrategy.sortHand(hand, direction, trumpSuit, config);
}
